// src/components/resources/specs/SpecsOverview.tsx

"use client"

import { type ReactNode } from 'react'
import { motion } from 'motion/react'
import RiInformationLine from '~/icons/remix/generated/RiInformationLine'
import RiCheckboxCircleLine from '~/icons/remix/generated/RiCheckboxCircleLine'
import RiErrorWarningLine from '~/icons/remix/generated/RiErrorWarningLine'
import RiPlayFill from '~/icons/remix/generated/RiPlayFill'
import RiHourglassFill from '~/icons/remix/generated/RiHourglassFill'
import RiLiveFill from '~/icons/remix/generated/RiLiveFill'
import RiRewindFill from '~/icons/remix/generated/RiRewindFill'
import { cn } from '~/lib/utils'

interface InfoCardProps {
  title: string
  description: ReactNode
  icon: typeof RiInformationLine
  className?: string
}

function InfoCard({ title, description, icon: Icon, className }: InfoCardProps): JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "rounded-xl bg-white p-6 shadow-lg ring-1 ring-gray-200 transition-shadow hover:shadow-xl dark:bg-gray-800 dark:ring-gray-700",
        className
      )}
      role="article"
    >
      <div className="flex items-start gap-4">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400"
          aria-hidden="true"
        >
          <Icon className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          <div className="mt-2 text-gray-600 dark:text-gray-400">
            {description}
          </div>
        </div>
      </div>
    </motion.div>
  )
}

/**
 * Minimal nav descriptor for the quick-navigation cards. Built from the guide's
 * own sections so the overview generalizes across platforms (Meta, TikTok, …)
 * instead of hardcoding one platform's formats.
 */
export interface SpecsOverviewSection {
  id: string
  title: string
  description?: string
}

interface SpecsOverviewProps {
  /** Page H1 — the guide's own title (e.g. "TikTok Video Ad Creative Specs Guide"). */
  title: string
  /** Lead paragraph under the H1 — the guide's description. */
  description: string
  /** Quick-nav cards, one per spec section, derived from the guide's sections. */
  sections: SpecsOverviewSection[]
}

// Rotating icon set so the quick-nav cards keep visual variety for any section
// count without depending on platform-specific metadata.
const NAV_ICONS = [RiPlayFill, RiHourglassFill, RiRewindFill, RiLiveFill] as const

export function SpecsOverview({ title, description, sections }: SpecsOverviewProps): JSX.Element {
  return (
    <div
      className="space-y-12"
      itemScope
      itemType="https://schema.org/TechArticle"
    >
      {/* Enhanced Introduction with SEO content */}
      <div className="relative space-y-8 overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-50/80 via-indigo-50/40 to-white px-8 py-12 dark:from-indigo-950/30 dark:via-gray-900 dark:to-indigo-950/30 sm:px-12">
        <div className="relative z-10 max-w-5xl">
          <h1
            className="bg-gradient-to-br from-gray-900 to-gray-600 bg-clip-text text-4xl font-extrabold tracking-tight text-transparent dark:from-white dark:to-gray-400 sm:text-5xl"
            itemProp="headline"
          >
            {title}
          </h1>
          <p
            className="mt-6 text-lg leading-relaxed text-gray-600 dark:text-gray-300 sm:text-xl"
            itemProp="description"
          >
            {description}
          </p>
        </div>

        {/* Decorative background elements */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute -left-4 top-24 h-72 w-72 rounded-full bg-indigo-400/10 blur-3xl dark:bg-indigo-600/10" />
          <div className="absolute -right-4 top-12 h-72 w-72 rounded-full bg-indigo-400/10 blur-3xl dark:bg-indigo-600/10" />
        </div>
      </div>

      {/* Key Information Cards */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" role="list">
        <InfoCard
          title="Why Video Specs Matter"
          icon={RiInformationLine}
          description={
            <ul className="space-y-2.5 text-sm" role="list" aria-label="Video specification benefits">
              <li className="flex items-center">
                <span className="mr-2 text-lg text-indigo-500" aria-hidden="true">•</span>
                <span>Ensure optimal video quality across all placements</span>
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-lg text-indigo-500" aria-hidden="true">•</span>
                <span>Maximize engagement with proper aspect ratios</span>
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-lg text-indigo-500" aria-hidden="true">•</span>
                <span>Avoid rejection due to technical requirements</span>
              </li>
            </ul>
          }
        />
        <InfoCard
          title="Creative Best Practices"
          icon={RiCheckboxCircleLine}
          description={
            <ul className="space-y-2.5 text-sm" role="list" aria-label="Video best practices">
              <li className="flex items-center">
                <span className="mr-2 text-lg text-indigo-500" aria-hidden="true">•</span>
                <span>Design for mobile-first viewing experience</span>
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-lg text-indigo-500" aria-hidden="true">•</span>
                <span>Include captions for accessibility and silent viewing</span>
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-lg text-indigo-500" aria-hidden="true">•</span>
                <span>Front-load key messages in first 3 seconds</span>
              </li>
            </ul>
          }
        />
        <InfoCard
          title="Common Video Issues"
          icon={RiErrorWarningLine}
          description={
            <ul className="space-y-2.5 text-sm" role="list" aria-label="Common video issues">
              <li className="flex items-center">
                <span className="mr-2 text-lg text-indigo-500" aria-hidden="true">•</span>
                <span>Low resolution or incorrect aspect ratio</span>
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-lg text-indigo-500" aria-hidden="true">•</span>
                <span>Missing captions for sound-off viewing</span>
              </li>
              <li className="flex items-center">
                <span className="mr-2 text-lg text-indigo-500" aria-hidden="true">•</span>
                <span>Video length exceeding placement limits</span>
              </li>
            </ul>
          }
        />
      </div>

      {/* Quick Navigation — generated from the guide's own sections */}
      <nav
        className="rounded-xl bg-gray-50 p-8 shadow-sm dark:bg-gray-800/50"
        aria-label="Video ad format specifications"
        role="navigation"
      >
        <div className="mb-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Video Ad Formats
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Click any format below to jump to detailed specifications, requirements, and best practices.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {sections.map((section, index) => {
            const Icon = NAV_ICONS[index % NAV_ICONS.length] ?? RiPlayFill
            const headingId = `heading-${section.id}`
            return (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="group rounded-xl border border-gray-200 bg-white p-6 transition-all hover:border-indigo-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-indigo-800"
                aria-labelledby={headingId}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900">
                    <Icon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <h4
                    id={headingId}
                    className="font-semibold text-gray-900 group-hover:text-indigo-600 dark:text-white dark:group-hover:text-indigo-400"
                  >
                    {section.title}
                  </h4>
                </div>

                {section.description ? (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {section.description}
                  </p>
                ) : null}
              </a>
            )
          })}
        </div>
      </nav>
    </div>
  )
}
